import React, { forwardRef, useImperativeHandle, useState, useEffect } from 'react';
import { Button, ButtonGroup } from '@progress/kendo-react-buttons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpRightAndDownLeftFromCenter } from '@fortawesome/free-solid-svg-icons';
import { DatePicker } from '@progress/kendo-react-dateinputs';
import moment from 'moment';
import AllEndpointActivityTabPage, { AllEndpointsActivityTabPageRef } from '../../../../SiteExplorer/Pages/Endpoints/Activity/AllEndpointsActivityTabPage';
import { EventFilter } from '../../../../Events/Utilities/EventsApiService';
import { enMessages } from '../../../../../messages/en-US';
import { useLocalization } from '@progress/kendo-react-intl';
import assetsDashboardApiService from '../../Utilities/AssetsDashboardApiService';
import { useQueries } from 'react-query';

interface AnalysisContentProps {
  selectedItems: Array<{ id: string; entityType: string }>;
  asset: any;
  isFullscreen: boolean;
  onExpand?: () => void;
  eventFilter: EventFilter;
  onStartDateChange: (event: any) => void;
  onEndDateChange: (event: any) => void;
}

export interface AnalysisContentRef {
  refresh: () => Promise<void>;
}

// Utility function for chart height calculation
const calculateChartHeight = (isFullscreen: boolean): number => {
  if (isFullscreen) {
    // For fullscreen mode, use viewport height minus UI elements
    const viewportHeight = window.innerHeight;
    return Math.max(300, viewportHeight - 385); // Min 400px, subtract UI space
  } else {
    // For regular mode, use a fixed height or calculate based on container
    const viewportHeight = window.innerHeight;
    return Math.max(300, viewportHeight - 415); // Min 400px, subtract UI space
  }
};

const AnalysisContent = forwardRef<AnalysisContentRef, AnalysisContentProps>(({
  selectedItems,
  asset,
  isFullscreen,
  onExpand,
  eventFilter,
  onStartDateChange,
  onEndDateChange
}, ref) => {
  const [selectedTab, setSelectedTab] = React.useState(0);
  const [chartHeight, setChartHeight] = useState(calculateChartHeight(isFullscreen));
  const localizationService = useLocalization();
  const activityTabRef = React.useRef<AllEndpointsActivityTabPageRef>(null);

  // Update chart height when fullscreen state changes
  useEffect(() => {
    setChartHeight(calculateChartHeight(isFullscreen));
  }, [isFullscreen]);

  // Update chart height on window resize
  useEffect(() => {
    const handleResize = () => {
      setChartHeight(calculateChartHeight(isFullscreen));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isFullscreen]);

  useImperativeHandle(ref, () => ({
    refresh: async () => {
      if (activityTabRef.current) {
        await activityTabRef.current.refresh();
      }
    }
  }));

  // Run queries for each selected item ID
  const endpointEventsStateChartDataQueries = useQueries(
    selectedItems.map((item) => ({
      queryKey: ["endpointEventsStateChartData", item.id, eventFilter.startDate, eventFilter.endDate],
      queryFn: () => assetsDashboardApiService.getEndpointEventsStateChartData(
        item.id, 
        eventFilter.startDate!, 
        eventFilter.endDate!
      ),
      enabled: !!item.id && !!eventFilter.startDate && !!eventFilter.endDate
    })) || []
  );

  // Get the first grease pump data where serialNo is not empty
  const greasePumpData = endpointEventsStateChartDataQueries
    .map(query => query.data)
    .find(data => data && data.serialNo && data.serialNo.trim() !== '');

  
  const renderTabContent = () => {
    if (selectedTab === 0) {
      // Extract IDs for the endpointsIds prop
      const endpointsIds = selectedItems.map(item => item.id);
      
      // Enhance selectedItems with correct entityType from asset configuration
      const enhancedSelectedItems = selectedItems.map(item => {
        // If entityType is already correct, use it
        if (item.entityType && item.entityType !== 'Unknown') {
          return item;
        }
        
        // Try to find endpoint in asset configuration
        const endpoint = asset?.assetConfigurationDetails?.endpoints?.find((ep: any) => 
          String(ep.id) === String(item.id)
        );
        
        if (endpoint?.entityType) {
          return {
            id: item.id,
            entityType: endpoint.entityType
          };
        }
        
        // Fallback: if asset is a Locomotive, assume endpoint is also Locomotive
        if (asset?.machineryType === 'Locomotive') {
          return {
            id: item.id,
            entityType: 'Locomotive'
          };
        }
        
        return item;
      });
      
      return (
        <div className="mx-2">
          <AllEndpointActivityTabPage 
            ref={activityTabRef}
            key={`${endpointsIds.join('-')}-AllEndpointActivityTabPage-${isFullscreen}`}
            eventFilter={eventFilter} 
            endpointsIds={endpointsIds}
            selectedAssetId={asset.id} 
            showTables={false}
            isFullscreen={isFullscreen}
            chartParentHeight={chartHeight}
            selectedItems={enhancedSelectedItems} // Pass enhanced selectedItems with correct entityType
            asset={asset} // Pass asset for machineryType fallback
          />
        </div>
      );
    }
    return (
      <div>
        <h2>{localizationService.toLanguageString('custom.diagnostics', enMessages.custom.diagnostics)}</h2>
        {/* Add diagnostics content here */}
      </div>
    );
  };

  return (
    <div key={`${selectedItems.map(item => item.id).join('-')}-AnalysisContent-${isFullscreen}`}>
      <div>
        {/* First row: Table/Chart, Date Picker, Expand */}
        <div className="flex flex-wrap justify-between items-center gap-y-2 my-2 mr-2">
          <div className="flex mx-2">
            <ButtonGroup>
              <Button 
                fillMode="solid"
                style={{
                  borderRadius: "0px",
                  backgroundColor: "transparent",
                  borderColor: "transparent",
                  borderBottom: selectedTab === 0 ? "2px solid var(--kendo-color-primary)" : "2px solid transparent",
                  color: selectedTab === 0 ? "var(--kendo-color-primary)" : "inherit"
                }}
                onClick={() => setSelectedTab(0)}>
                {localizationService.toLanguageString('custom.activity', enMessages.custom.activity)}
              </Button>
            </ButtonGroup>
          </div>
          <div className="flex items-end gap-2 ml-auto">
            <div className="flex items-center gap-2">
              <DatePicker
                value={eventFilter.startDate}
                onChange={onStartDateChange}
                max={eventFilter.endDate || undefined}
                width="150px"
              />
              <span>-</span>
              <DatePicker
                value={eventFilter.endDate}
                onChange={onEndDateChange}
                min={eventFilter.startDate || undefined}
                max={moment().toDate()}
                width="150px"
              />
            </div>

            {!isFullscreen && onExpand && (
              <Button onClick={onExpand}>
                <FontAwesomeIcon icon={faUpRightAndDownLeftFromCenter} />
              </Button>
            )}
          </div>
        </div>
        <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
});

export default AnalysisContent; 